import React from 'react';
import { Box, Grid, Typography, IconButton } from '@mui/material';
import { TwCommoditySelector } from '../../../TwCommodityTableSelector/TwCommodityTableSelector';
import { CommodityTableAttributes, CommodityList, CommodityUnit } from '../../../../redux/api/commodities/commodities.model';
import { MeasurementSystem } from '../../../../providers/User/User.model';
import { useCommoditySettings } from '../../../../hooks/useCommoditySettings';
import { imperialToCm, mapCommodityTableAttributesToCommodityList, unitOptions } from '../../../../utils/commodity/commodityUtils';
import CloseIcon from '@mui/icons-material/Close';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { TwInputTypes } from '../../../TwFields/TwFields';
import TwField from '../../../TwFields/TwFields';
import TwMetricSystemInput from '../../../TwMetricSystemInput/TwMetricSystemInput';
import TwImperialSystemInput from '../../../TwImperialSystemInput/TwImperialSystemInput';
import TwCheckbox from '../../../TwCheckboxes/TwCheckboxes';
import TwSelect from '../../../TwSelects/TwSelects';
import { useAppSelector } from '../../../../redux/hooks';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateNewOrderCommodities } from '../../../../redux/features/orders/ordersSlice';
import { styled } from '@mui/material/styles';
import { EntityType } from '../../../../redux/Global.model';
import { updateNewTripCommodity } from '../../../../redux/features/trip/tripSlice';
import { useState, useEffect } from 'react';

interface TwMobileCommodityRowProps {
  id: string;
  commodity: CommodityList;
  measurementSystem: MeasurementSystem;
  entity: EntityType;
}

// Style constants
const WIDTH_LABEL = '25%';
const WIDTH_INPUT = '75%';
const WIDTH_QTY = '15%';
const WIDTH_WEIGHT = '30%';
const COLOR_ICON = '#7E858E';
const COLOR_UNIT = '#9CA3AF';

// Reusable styled components
const StyledRowBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8, // gap={1} in MUI equals 8px
});
const StyledLabelBox = styled(Box)(() => ({
  width: WIDTH_LABEL,
}));
const StyledInputBox = styled(Box)(() => ({
  width: WIDTH_INPUT,
}));
const StyledQtyBox = styled(Box)(() => ({
  width: WIDTH_QTY,
}));
const StyledWeightBox = styled(Box)(() => ({
  width: WIDTH_WEIGHT,
}));

const TwMobileCommodityRow: React.FC<TwMobileCommodityRowProps> = ({
  id,
  commodity,
  measurementSystem,
  entity
}) => {
  const { new_order_commodities = [] } = useAppSelector((state) => state.orders);
  const { new_trip_commodities = [] } = useAppSelector((state) => state.trip);

  const commoditiesToUse =
    entity === EntityType.ORDER ? new_order_commodities : new_trip_commodities;

  const dispatch = useAppDispatch();
  const { isDimensionsEnabled, isUnitEnabled, isPermitRequirementEnabled, isMakeEnabled, isModelEnabled } = useCommoditySettings();

  // Local state for all editable fields
  const [localQuantity, setLocalQuantity] = useState(commodity.quantity?.toString() ?? '');
  const [localWeight, setLocalWeight] = useState(commodity.weight ?? '');
  const [localUnit, setLocalUnit] = useState(commodity.unit ?? '');
  const [localNotes, setLocalNotes] = useState(commodity.notes ?? '');
  const [localPermit, setLocalPermit] = useState(!!commodity.permit);

  // Sync local state if commodity changes from outside
  useEffect(() => {
    setLocalQuantity(commodity.quantity?.toString() ?? '');
    setLocalWeight(commodity.weight ?? '');
    setLocalUnit(commodity.unit ?? '');
    setLocalNotes(commodity.notes ?? '');
    setLocalPermit(!!commodity.permit);
  }, [commodity]);

  const handleCommodityDescriptionChange = (description: string): void => {
    const updatedCommodities = commoditiesToUse.map((c) =>
      c.id === id ? { ...c, name: description } : c
    );
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  // Field blur handlers to update Redux only if value changed
  const handleQuantityBlur = (): void => {
    const parsedQuantity = localQuantity === '' ? null : Number(localQuantity);
    if (parsedQuantity !== (commodity.quantity ?? null)) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === id ? { ...c, quantity: parsedQuantity } : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    }
  };

  const handleWeightBlur = (): void => {
    const parsedWeight = localWeight === '' ? null : Number(localWeight);
    if (parsedWeight !== (commodity.weight ?? null)) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === id ? { ...c, weight: parsedWeight } : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    }
  };

  const handleUnitChange = (value: string): void => {
    setLocalUnit(value);
    const found = unitOptions.find((opt) => opt.value === value);
    const unitValue: CommodityUnit | null = found ? found.value as CommodityUnit : null;
    if (unitValue !== (commodity.unit ?? null)) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === id ? { ...c, unit: unitValue } : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    }
  };

  const handleNotesBlur = (): void => {
    if (localNotes !== (commodity.notes ?? '')) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === id ? { ...c, notes: localNotes } : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    }
  };

  const handlePermitChange = (checked: boolean): void => {
    setLocalPermit(checked);
    if (checked !== !!commodity.permit) {
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === id ? { ...c, permit: checked } : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    }
  };

  const handleCommoditySelect = (
    option: CommodityTableAttributes | undefined,
    targetId: string
  ): void => {
    if (option) {
      const mappedCommodity = mapCommodityTableAttributesToCommodityList(
        option,
        targetId,
        commodity.id
      );
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === commodity.ui_uid ? mappedCommodity : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    } else {
      const targetCommodity = commoditiesToUse.find((c) => c.ui_uid === commodity.ui_uid);
      if (targetCommodity) {
        targetCommodity.name = '';
        const updatedCommodities = commoditiesToUse.map((c) =>
          c.ui_uid === commodity.ui_uid ? { ...c, ...targetCommodity } : c
        );
        if (entity === EntityType.ORDER) {
          dispatch(updateNewOrderCommodities(updatedCommodities));
        } else {
          dispatch(updateNewTripCommodity(updatedCommodities));
        }
      }
    }
  };

  const handleImperialChange = (
    e: { leftValue: string; rightValue: string },
    type: 'length' | 'width' | 'height'
  ): void => {
    const cm = imperialToCm(Number(e.leftValue), Number(e.rightValue));
    const updatedCommodity = {
      ...commodity,
      [type]: cm.toFixed(2),
      [`${type}_right`]: e.rightValue,
      [`${type}_left`]: e.leftValue
    };
    const updatedCommodities = commoditiesToUse.map((c) =>
      c.ui_uid === id ? updatedCommodity : c
    );
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  const handleSetCommodityDocument = (commodityId: string): void => {
    // TODO: Implement this
  };

  const handleRemoveCommodity = (commodityId: string): void => {
    const updatedCommodities = commoditiesToUse.filter((c) => c.id !== commodityId);
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  // Restore handleFieldChange for non-migrated fields (make, model, dimensions, etc.)
  const handleFieldChange = (commodityId: string, field: string, value: string | number | boolean): void => {
    // Validation for quantity and weight: only positive numbers
    if (field === 'quantity' || field === 'weight') {
      const num = Number(value);
      if (isNaN(num) || num < 0) {
        return; // Do not update if not valid
      }
    }
    const updatedCommodities = commoditiesToUse.map((c) =>
      c.ui_uid === commodityId ? { ...c, [field]: value } : c
    );
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  return (
    <Box p={2} mb={2} borderRadius={2} boxShadow={1} bgcolor="background.paper">
      {/* Commodity type selector */}
      <Box mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1}>
            <TwCommoditySelector
              value={commodity.commodity_id}
              textValue={commodity.name}
              onSelect={handleCommoditySelect}
              onChange={handleCommodityDescriptionChange}
              id={id}
              commodityId={commodity.readable_commodity_id}
            />
          </Box>
          <Box>
            {/* TODO: Implement this later */}
            {/* <IconButton
              title="View commodity document"
              aria-label="View commodity document"
              onClick={(): void => handleSetCommodityDocument(commodity.id || '')}
            >
              <AttachmentIcon
                sx={{
                  color: COLOR_ICON,
                  transform: 'rotate(-40deg)',
                  opacity: '50%'
                }}
              />
            </IconButton> */}
          </Box>
          <Box>
            <IconButton
              onClick={(): void => handleRemoveCommodity(id)}
              sx={{ mt: '-5px' }}
              size="small"
              aria-label="Remove commodity"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Make and Model */}
      {(isMakeEnabled || isModelEnabled) && (
        <Grid container spacing={1} mb={2}>
          {isMakeEnabled && (
            <Grid item xs={12}>
              {/* Input Make */}
              <StyledRowBox>
                <StyledLabelBox>
                  <Typography variant="body2">Make</Typography>
                </StyledLabelBox>
                <StyledInputBox>
                  <TwField
                    id="make"
                    name="make"
                    value={commodity.make || ''}
                    onInputChange={(e): void => handleFieldChange(id, 'make', e.target.value)}
                    inputType={TwInputTypes.text}
                    placeholder=""
                  />
                </StyledInputBox>
              </StyledRowBox>
            </Grid>
          )}
          {isModelEnabled && (
            <Grid item xs={12}>
              {/* Input Model */}
              <StyledRowBox>
                <StyledLabelBox>
                  <Typography variant="body2">Model</Typography>
                </StyledLabelBox>
                <StyledInputBox>
                  <TwField
                    id="model"
                    name="model"
                    value={commodity.model || ''}
                    onInputChange={(e): void => handleFieldChange(id, 'model', e.target.value)}
                    inputType={TwInputTypes.text}
                    placeholder=""
                  />
                </StyledInputBox>
              </StyledRowBox>
            </Grid>
          )}
        </Grid>
      )}

      {/* Dimensions */}
      {isDimensionsEnabled && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {/* Input Length */}
            <StyledRowBox mb={2}>
              <StyledLabelBox>
                <Typography variant="body2">Length</Typography>
              </StyledLabelBox>
              <StyledInputBox>
                {measurementSystem === MeasurementSystem.IMPERIAL ? (
                  <TwImperialSystemInput
                    sx={{ width: '50%' }}
                    onChange={(e): void => handleImperialChange(e, 'length')}
                    measurementSystem={measurementSystem}
                    leftValue={commodity.length_left ?? ''}
                    rightValue={commodity.length_right ?? ''}
                  />
                ) : (
                  <TwMetricSystemInput
                    cmValue={commodity.length?.toString() ?? ''}
                    onChange={(e): void => handleFieldChange(id, 'length', e)}
                    externalError={''}
                  />
                )}
              </StyledInputBox>
            </StyledRowBox>
          </Grid>
          <Grid item xs={12}>
            {/* Input Width */}
            <StyledRowBox mb={2}>
              <StyledLabelBox>
                <Typography variant="body2">Width</Typography>
              </StyledLabelBox>
              <StyledInputBox>
                {measurementSystem === MeasurementSystem.IMPERIAL ? (
                  <TwImperialSystemInput
                    sx={{ width: '50%' }}
                    onChange={(e): void => handleImperialChange(e, 'width')}
                    measurementSystem={measurementSystem}
                    leftValue={commodity.width_left ?? ''}
                    rightValue={commodity.width_right ?? ''}
                  />
                ) : (
                  <TwMetricSystemInput
                    cmValue={commodity.width?.toString() ?? ''}
                    onChange={(e): void => handleFieldChange(id, 'width', e)}
                    externalError={''}
                  />
                )}
              </StyledInputBox>
            </StyledRowBox>
          </Grid>
          <Grid item xs={12}>
            {/* Input Height */}
            <StyledRowBox mb={2}>
              <StyledLabelBox>
                <Typography variant="body2">Height</Typography>
              </StyledLabelBox>
              <StyledInputBox>
                {measurementSystem === MeasurementSystem.IMPERIAL ? (
                  <TwImperialSystemInput
                    sx={{ width: '50%' }}
                    onChange={(e): void => handleImperialChange(e, 'height')}
                    measurementSystem={measurementSystem}
                    leftValue={commodity.height_left ?? ''}
                    rightValue={commodity.height_right ?? ''}
                  />
                ) : (
                  <TwMetricSystemInput
                    cmValue={commodity.height?.toString() ?? ''}
                    onChange={(e): void => handleFieldChange(id, 'height', e)}
                    externalError={''}
                  />
                )}
              </StyledInputBox>
            </StyledRowBox>
          </Grid>
        </Grid>
      )}

      {/* Permit */}
      {isPermitRequirementEnabled && (
        <StyledRowBox mb={2}>
          <StyledLabelBox>
            <Typography variant="body2">Permit</Typography>
          </StyledLabelBox>
          <StyledInputBox>
            <TwCheckbox
              name="permit"
              label="Required"
              checked={localPermit}
              onInputChange={(e): void => handlePermitChange(e.target.checked)}
            />
          </StyledInputBox>
        </StyledRowBox>
      )}

      {/* Note */}
      <StyledRowBox mb={2}>
        <StyledLabelBox>
          <Typography variant="body2">Note</Typography>
        </StyledLabelBox>
        <StyledInputBox>
          <TwField
            id="notes"
            name="notes"
            value={localNotes}
            onInputChange={(e): void => setLocalNotes(e.target.value)}
            onInputBlur={handleNotesBlur}
            inputType={TwInputTypes.text}
            placeholder=""
          />
        </StyledInputBox>
      </StyledRowBox>

      {/* Unit */}
      {isUnitEnabled && (
        <StyledRowBox mb={2}>
          <StyledLabelBox>
            <Typography variant="body2">Unit</Typography>
          </StyledLabelBox>
          <StyledInputBox>
            <TwSelect
              id="unit"
              name="unit"
              value={localUnit}
              onInputChange={(e): void => handleUnitChange(e.target.value)}
              optionList={unitOptions}
            />
          </StyledInputBox>
        </StyledRowBox>
      )}

      {/* Qty x Weight */}
      <StyledRowBox mb={2}>
        <StyledLabelBox>
          <Typography variant="body2">Qty x Weight</Typography>
        </StyledLabelBox>
        <StyledQtyBox>
          <TwField
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                lineHeight: 1.5
              }
            }}
            id="qty"
            name="qty"
            value={localQuantity}
            onInputChange={(e): void => setLocalQuantity(e.target.value)}
            onInputBlur={handleQuantityBlur}
            inputType={TwInputTypes.number}
            placeholder=""
          />
        </StyledQtyBox>
        <StyledQtyBox sx={{ textAlign: 'center' }}>
          <Typography variant="body2">x</Typography>
        </StyledQtyBox>
        <StyledWeightBox>
          <TwField
            sx={{
              '& .MuiInputBase-input': {
                textAlign: 'center',
                lineHeight: 1.5
              }
            }}
            id="weight"
            name="weight"
            value={localWeight}
            onInputChange={(e): void => setLocalWeight(e.target.value)}
            onInputBlur={handleWeightBlur}
            inputType={TwInputTypes.number}
            endAdorment={
              MeasurementSystem.IMPERIAL === measurementSystem ? (
                <Typography color={COLOR_UNIT} sx={{ fontSize: '14px' }}>
                  lb
                </Typography>
              ) : (
                <Typography color={COLOR_UNIT} sx={{ fontSize: '14px' }}>
                  kg
                </Typography>
              )
            }
          />
        </StyledWeightBox>
      </StyledRowBox>
    </Box>
  );
};

export default TwMobileCommodityRow;
